import { DOCUMENT } from '@angular/common';
import { Component, Inject, type OnInit, type OnDestroy, Renderer2 } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import type { Subscription } from 'rxjs';
import { catchError, EMPTY, filter, first } from 'rxjs';
import { environment } from 'src/environments/environment';
import type { ClinicInterface } from '../../models/clinic.interface';
import { NavItemName } from '../../models/navItemName-enum';
import { NavPrefDefault } from '../../models/navPrefDefault';
import type { NotificationInterface } from '../../models/notification.interface';
import type { NavPref, UserPreferences } from '../../models/preferences.interface';
import { AppStateService } from '../../services/app-state.service';
import { ApplicationService } from '../../services/application.service';
import { ClinicService } from '../../services/clinic.service';
import { AdminClinicService } from '../../../components/clinics/services/admin-clinic.service';
import type { ClinicWithOrthancModalityInterface } from '../../../components/clinics/models/admin-clinic.interface';
import { NotificationService } from '../../services/notification.service';
import { PreferencesService } from '../../services/preferences.service';
import { SnackbarService } from '../../services/snackbar.service';
import { TokenStorageService } from '../../services/token-storage.service';
import { UserService } from '../../services/user.service';
import { BugReportDialogComponent } from '../bug-report-dialog/bug-report-dialog.component';
import { SupportContactDialogComponent } from '../support-contact-dialog/support-contact-dialog.component';
import { modeType } from '../../../../environments/environment.interface';
import { includesIgnoringAccents } from '../../utils';
import { ZendeskService } from '../../services/zendesk.service';

@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss']
})
export class NavigationComponent implements OnInit, OnDestroy {
  pinnedItems: NavPref[] = [];
  apiUrl = environment.apiUrl;
  NavItemName = NavItemName;

  userName?: string;
  clinicName?: string;
  preferences: UserPreferences;

  localPacsAvailable = true;

  selectedClinic: ClinicInterface;
  clinics: ClinicInterface[] = [];
  filteredClinics: ClinicInterface[] = [];
  myPacsClinics: ClinicInterface[] = [];
  filteredMyPacsClinics: ClinicInterface[] = [];
  groupedClinics: Record<string, ClinicInterface[]> = {};
  clinicSearchTerm = '';

  notifications: NotificationInterface[] = [];
  isNotificationsLeft = false;
  notificationTotalCount = 0;

  environment: string = environment.name;
  mode: modeType;
  isProd = environment.production;

  canReadClinic: boolean;
  canCreateUser: boolean;
  canCreateReportTemplate: boolean;
  canUploadStudyScc: boolean;
  canReadMailConfiguration: boolean;
  canSeeBilling: boolean;
  hasNoClinics = false;
  isLiteClinic = false;
  public isScc = false;
  public isFinalizingAccount = false;

  private static readonly LITE_RESTRICTED_NAV_ITEMS: NavItemName[] = [
    NavItemName.ATLAS,
    NavItemName.USERS,
    NavItemName.VETS,
    NavItemName.DICOMIZER,
    NavItemName.RADIO,
  ];

  public isAdmin = false;
  public isPms = false;
  public selectedLanguage = this.translateService.currentLang || 'fr';

  private notificationSkip = 0;
  private get notificationTake() {
    return 3;
  }

  private notificationsSubscription: Subscription;
  private clinicAddedSubscription: Subscription;
  private someMenuPinned: boolean;
  private externalLogin: string | undefined;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly renderer: Renderer2,
    private readonly tokenStorage: TokenStorageService,
    private readonly appState: AppStateService,
    private readonly applicationService: ApplicationService,
    private readonly preferencesService: PreferencesService,
    private readonly clinicService: ClinicService,
    private readonly adminClinicService: AdminClinicService,
    private readonly dialog: MatDialog,
    private readonly notificationService: NotificationService,
    private readonly snackbarService: SnackbarService,
    private readonly userService: UserService,
    private readonly dateAdapter: DateAdapter<any>,
    public translateService: TranslateService,
    private readonly router: Router,
    private readonly zendeskService: ZendeskService
  ) {
    this.mode = this.appState.mode;
    this.isScc = this.appState.isScc;
  }

  ngOnInit(): void {
    if (this.isScc) {
      this.updateFinalizingState(this.router.url);
      this.router.events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd)
      ).subscribe(event => this.updateFinalizingState(event.urlAfterRedirects));
    }

    this.appState.claimsObservable.subscribe(claims => {
      this.isAdmin = claims.includes('roles=Admin');
      this.isPms = claims.includes('roles=PmsDeveloper') || this.isAdmin;
      this.canReadClinic = claims.includes('Clinic=Read');
      this.canCreateUser = claims.includes('User=Create');
      this.canCreateReportTemplate = claims.includes('ReportTemplate=Create');
      this.canUploadStudyScc = claims.includes('roles=SccVeterinary');
      this.canReadMailConfiguration = claims.includes('MailConfiguration=Read');
      this.canSeeBilling = this.isAdmin || claims.includes('roles=ClinicAdmin');

      const externalLoginClaim = claims.find(c => c.includes('ExternalLogin'));
      this.externalLogin = externalLoginClaim ? externalLoginClaim.split('=')[1] : undefined;
    });

    this.appState.userNameObservable.subscribe(userName => {
      if (userName) {
        this.userName = userName;
        // this.tokenStorage.saveUser(userName);
      }
    });

    this.appState.clinicObservable.subscribe(clinic => {
      const wasLite = this.isLiteClinic;
      this.isLiteClinic = !!clinic?.isLite;
      if (wasLite !== this.isLiteClinic && this.preferences?.preferenciesObject) {
        this.filterPinnedItems();
      }
    });

    this.appState.clinicLogoObservable.subscribe(clinicLogoChange => {
      if (clinicLogoChange) {
        const clinic = this.clinics.find(c=>c.id === clinicLogoChange.id);
        if (clinic) {
          clinic.logoUrl = clinicLogoChange.logoUrl;
        }
      }
    });

    this.tokenStorage.user.subscribe(user => {
      this.userName = user;

      if (!user) {
        return;
      }

      this.zendeskService.authenticate();

      this.preferences = {
        selectedClinicName: '',
        selectedClinicId: 0,
        preferenciesObject: new NavPrefDefault(),
        locale: this.translateService.currentLang,
        securityInfo:
        {
          claims: [],
          roles: [],
          roleIds: []
        },
        signature: '',
        firstName: '',
        lastName: ''
      };

      this.preferencesService.getPreferences().pipe(
        // Évite qu'une erreur (ex. token expiré en cours de session) laisse le shell figé
        // sans feedback : on log sans déconnecter (la déconnexion reste gérée par l'interceptor).
        catchError((e) => {
          console.error('[Navigation] Échec du chargement des préférences', e);
          return EMPTY;
        })
      ).subscribe((pref) => {
        this.selectedLanguage = pref.locale;
        this.translateService.use(pref.locale);
        this.dateAdapter.setLocale(pref.locale);
        this.appState.claims = pref.securityInfo.claims;
        this.appState.roles = pref.securityInfo.roles;
        this.appState.roleIds = pref.securityInfo.roleIds;
        this.appState.signature = pref.signature;
        this.userName = `${pref.firstName} ${pref.lastName}`;

        if (!pref.preferenciesObject) {
          pref.preferenciesObject = new NavPrefDefault();
        }

        const oldMenu = pref.preferenciesObject.navPref;

        pref.preferenciesObject.navPref = new NavPrefDefault().navPref;

        if (!this.canCreateReportTemplate) {
          pref.preferenciesObject.navPref = pref.preferenciesObject.navPref.filter(n=> n.navItem  !== NavItemName.TEMPLATES );
        }

        if (!this.canCreateUser) {
          pref.preferenciesObject.navPref = pref.preferenciesObject.navPref.filter(n=> n.navItem  !== NavItemName.USERS );
        }

        if (!this.canReadMailConfiguration) {
          pref.preferenciesObject.navPref = pref.preferenciesObject.navPref.filter(n=> n.navItem  !== NavItemName.TXT_MAIL );
        }

        // suppression temporaire des menus non implementés
        pref.preferenciesObject.navPref = pref.preferenciesObject.navPref.filter(n=> n.navItem  !== NavItemName.BLOC_MAIL );
        // pref.preferenciesObject.navPref = pref.preferenciesObject.navPref.filter(n=> n.navItem  !== NavItemName.VETS );

        oldMenu.forEach(oldItemmenu => {
          const newItemMenu = pref.preferenciesObject.navPref.find(i => i.navItem === oldItemmenu.navItem);
          if (newItemMenu) {
            newItemMenu.pinned = oldItemmenu.pinned;
          }
        });

        this.preferences = pref;

        this.applyClinic(this.preferences.selectedClinicId);
        this.applyTheme(this.preferences.preferenciesObject.isDarkMode);
        this.filterPinnedItems();
      });

      this.ping();
      setInterval(() => {
        this.ping();
      }, environment.pingInterval);

      this.getNotifications();

      this.notificationService.notificationAdded.subscribe(notification => {
        this.notifications = [];
        this.notificationSkip = 0;
        this.getNotifications();
        let message = '';
        let infos;

        switch (notification.code) {
          case 'NEW_STUDY':
            if (!this.appState.isScc) {
              infos = notification.param.split(',');
              message = `${this.translateService.instant('mainNav.notificationsMenu.new_study') as string}${infos[0]} - ${infos[1]}`;
            }

            break;
          case 'TEXT':
            message = this.getTextMessage(notification);
            break;

          case 'SHARE_STUDY':
            infos = notification.param.split(',');
            message = `${this.translateService.instant('mainNav.snackbar.share') as string}${infos[0]} - ${infos[1]}(${infos[2]})`;
            break;

          case 'SHARE_ALBUM':
            infos = notification.param.split(',');
            message = `${this.translateService.instant('mainNav.snackbar.SHARE_ALBUM') as string}${infos[0]} - ${this.translateService.instant(`AlbumTypeEnum.${infos[1].toUpperCase()}`) as string} (${infos[2]})`;
            break;

          case 'UPDATE':
            message = this.translateService.instant('mainNav.snackbar.update') as string;
            break;

          default:
            break;
        }

        if (message) {
          this.snackbarService.openSnackBar(message);
        }
      });

      this.setTrad();
    });

    // Subscribe to translate service language changes to keep selectedLanguage in sync
    this.translateService.onLangChange.subscribe((event) => {
      this.selectedLanguage = event.lang;
    });

    this.appState.preferencesObservable.subscribe(preferences => {
      if (preferences && this.preferences) {
        this.preferences.preferenciesObject = preferences;
      }
    });

    if (environment.name.includes('scc')) {
      this.clinicService.selectedClinicChanged$.subscribe(clinic => {
        this.clinicChangeCommon(clinic);
      });
    }

    // Écoute les événements d'ajout de clinique
    this.clinicAddedSubscription = this.clinicService.clinicAdded$.subscribe(
      (newClinic: ClinicWithOrthancModalityInterface) => {
        this.onClinicAdded(newClinic);
      }
    );
  }

  getTextMessage(notification: NotificationInterface) {
    const params = notification.param.split(',');
    const message = 'mainNav.notificationsMenu.' + (notification.code + '.' + params[0]).toLowerCase();
    if (params.length > 1) {
      const args: Record<string, string> = {};
      const idx = 0;
      params.slice(1).forEach(p=> {
        args[`arg${idx}`] = p;
      });

      return this.translateService.instant(message, args) as string;
    } else {
      return this.translateService.instant(message) as string;
    }
  }

  getNavItem(navItemName: number) {
    return navItemName as NavItemName;
  }

  getNotifications() {
    if (this.notificationsSubscription) {
      this.notificationsSubscription.unsubscribe();
    }

    this.notificationsSubscription = this.notificationService.getNotifications(this.notificationSkip, this.notificationTake).subscribe(notificationsResponse => {
      this.notifications = [...this.notifications, ...notificationsResponse.items];
      this.notificationSkip += this.notificationTake;
      this.isNotificationsLeft = notificationsResponse.pageInfo.hasNextPage;
      this.notificationTotalCount = notificationsResponse.totalCount;
    });
  }

  closeAllNotifications() {
    this.notificationService.closeAllNotifications().subscribe(() => {

      this.notifications = [];
      this.notificationSkip = 0;

      this.getNotifications();
    });
  }

  ping() {
    // first() : un seul résultat puis désabonnement automatique. Référencer la
    // Subscription dans le callback (auto-unsubscribe) plante si l'émission est
    // synchrone (TDZ) — cas du ping court-circuité en mode SCC.
    this.applicationService.ping()
      .pipe(first())
      .subscribe(result => {
        this.localPacsAvailable = result?.ok ?? false;
      });
  }

  // Pin item event, allow pinned item only if items list is lower than 6 items
  pinItem(event: Event, item: NavPref): void {
    event.stopPropagation();
    const pinned = !item.pinned;

    this.preferences.preferenciesObject.navPref.forEach((pref) => {
      if (pref.navItem === item.navItem) {
        if (pinned) {
          pref.pinned = this.pinnedItems.length < 6;
        } else {
          pref.pinned = pinned;
        }
      }
    });

    this.filterPinnedItems();
    this.preferencesService.getPreferences();
    this.someMenuPinned = true;
  }

  switchTheme(): void {
    const newValue = !this.preferences.preferenciesObject.isDarkMode;
    this.preferences.preferenciesObject.isDarkMode = newValue;
    this.applyTheme(newValue);
    this.savePreferences();
  }

  applyTheme(isDarkMode: boolean) {
    if (isDarkMode && !this.isScc) {
      this.renderer.addClass(this.document.body, 'dark-theme');
    } else {
      this.renderer.removeClass(this.document.body, 'dark-theme');
    }
  }

  onMenuClick(event: Event): void {
    event.stopPropagation();
  }

  menuOpened(): void {
    this.someMenuPinned = false;
  }

  menuClosed(): void {
    if (this.someMenuPinned) {
      this.someMenuPinned = false;
      this.savePreferences();
    }
  }

  savePreferences() {
    this.preferencesService.savePreferences(this.preferences.preferenciesObject).pipe(first()).subscribe(preferences => {
      this.appState.preferences = preferences?.preferenciesObject;
    });
  }

  clinicChange(change: { value: ClinicInterface }) {
    this.clinicChangeCommon(change.value);
  }

  clinicChangeCommon(clinic: ClinicInterface ) {
    this.clinicName = clinic.name;
    this.appState.clinicId = clinic.id;
    this.appState.clinic = clinic;
    this.preferencesService.updateSelectClinic(clinic.id).pipe(first()).subscribe();
  }

  logout() {
    this.tokenStorage.signOut();
    this.zendeskService.logout();

    switch (this.externalLogin) {
      case 'OrdreVeterinaire':
        window.location.href = 'https://auth.sso.veterinaire.fr/oauth2/logout';
        break;
      default:
        window.location.href = '/';
    }
  }

  showBugReportModal(): void {
    this.dialog.open(BugReportDialogComponent);
  }

  showSupportModal(): void {
    this.dialog.open(SupportContactDialogComponent);
  }

  onNotificationClosed(notificationId: number) {
    this.notificationService.closeNotification(notificationId, this.notificationSkip).subscribe(_ => {
      this.notifications = [];
      this.notificationSkip = 0;
      this.getNotifications();
    });
  }

  switchLang(lang: string): void {
    this.selectedLanguage = lang;
    this.translateService.use(lang);
    this.dateAdapter.setLocale(lang);
    this.zendeskService.setLocale(lang);
    this.zendeskService.updateServerLocale(lang);

    // Save language preference to localStorage
    localStorage.setItem('user-language', lang);

    this.userService.updateUserConnected({
      locale: lang
    }).subscribe();
  }


  stopPropagation(e: any) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    e.stopPropagation();
  }

  filterClinics(): void {
    if (!this.clinicSearchTerm) {
      this.filteredClinics = [...this.clinics];
      this.filteredMyPacsClinics = [...this.myPacsClinics];
    } else {
      const filterFn = (clinic: ClinicInterface) => {
        if (includesIgnoringAccents(clinic.shortName, this.clinicSearchTerm)) {
          return true;
        }
        if (includesIgnoringAccents(clinic.name, this.clinicSearchTerm)) {
          return true;
        }
        if (clinic.parent?.name && includesIgnoringAccents(clinic.parent.name, this.clinicSearchTerm)) {
          return true;
        }
        return false;
      };

      this.filteredClinics = this.clinics.filter(filterFn);
      this.filteredMyPacsClinics = this.myPacsClinics.filter(filterFn);
    }

    this.groupClinics();
  }

  groupClinics(): void {
    this.groupedClinics = {};
    const standaloneClinics: ClinicInterface[] = [];
    const groupClinicsByName: Record<string, ClinicInterface> = {};

    // First pass: identify ALL group clinics and process them first
    // Group clinics should always be top-level, regardless of their parent
    const groupClinicsList = this.filteredClinics.filter(c => c.isGroup);

    groupClinicsList.forEach(clinic => {
      groupClinicsByName[clinic.name] = clinic;
      // Create the group entry immediately with the group clinic as metadata
      if (!this.groupedClinics[clinic.name]) {
        this.groupedClinics[clinic.name] = [];
        (this.groupedClinics[clinic.name] as any).groupClinic = clinic;
      }
    });

    // Second pass: process non-group clinics
    this.filteredClinics.filter(c => !c.isGroup).forEach(clinic => {
      if (clinic.parent?.name) {
        // Use parent name from the clinic data
        const groupName = clinic.parent.name;

        if (!this.groupedClinics[groupName]) {
          this.groupedClinics[groupName] = [];
        }

        // If there's a group clinic with this name, attach it as metadata
        if (groupClinicsByName[groupName]) {
          (this.groupedClinics[groupName] as any).groupClinic = groupClinicsByName[groupName];
        }

        this.groupedClinics[groupName].push(clinic);
      } else {
        standaloneClinics.push(clinic);
      }
    });

    // Handle standalone clinics
    if (Object.keys(this.groupedClinics).length > 0 && standaloneClinics.length > 0) {
      this.groupedClinics['Cliniques indépendantes'] = standaloneClinics;
    } else if (standaloneClinics.length > 0) {
      // If there are only standalone clinics, don't group them
      this.groupedClinics[''] = standaloneClinics;
    }
  }

  shouldShowGroupHeader(groupName: string): boolean {
    // Always show group headers when there's a search term and the group has a name
    if (this.clinicSearchTerm && groupName) {
      return true;
    }

    // Show group headers when there are multiple groups OR when there's a selectable group clinic
    const hasMultipleGroups = this.getGroupNames().length > 1;
    const hasSelectableGroupClinic = !!this.getGroupClinic(groupName);

    return (hasMultipleGroups || hasSelectableGroupClinic) && !!groupName;
  }

  getGroupNames(): string[] {
    return Object.keys(this.groupedClinics);
  }

  getGroupClinic(groupName: string): ClinicInterface | undefined {
    const group = this.groupedClinics[groupName];
    return group ? (group as any).groupClinic : undefined;
  }

  selectGroupClinic(event: Event, groupClinic: ClinicInterface): void {
    event.stopPropagation();
    this.selectedClinic = groupClinic;
    this.clinicChange({ value: groupClinic });
  }

  getFlagIcon(lang: string): string {
    const flags: Record<string, string> = {
      en: '🇬🇧', // UK flag for English
      fr: '🇫🇷', // French flag
      de: '🇩🇪', // German flag
      lb: '🇱🇺'  // Luxembourg flag
    };
    return flags[lang] || '🌐';
  }

  onClinicMenuOpened(): void {
    // Reset search term when opening the clinic selector
    this.clinicSearchTerm = '';
    this.filterClinics();
  }

  ngOnDestroy(): void {
    // Nettoyage des subscriptions pour éviter les fuites mémoire
    if (this.notificationsSubscription) {
      this.notificationsSubscription.unsubscribe();
    }

    if (this.clinicAddedSubscription) {
      this.clinicAddedSubscription.unsubscribe();
    }
  }

  // Sort only pinned links for navigation display
  private filterPinnedItems(): void {
    let items = this.preferences.preferenciesObject.navPref.filter((item) => item.pinned);
    if (this.isLiteClinic) {
      items = items.filter(item => !NavigationComponent.LITE_RESTRICTED_NAV_ITEMS.includes(item.navItem));
    }
    this.pinnedItems = items;
  }

  get displayedNavPref(): NavPref[] {
    const navPref = this.preferences?.preferenciesObject?.navPref ?? [];
    if (!this.isLiteClinic) {
      return navPref;
    }
    return navPref.filter(item => !NavigationComponent.LITE_RESTRICTED_NAV_ITEMS.includes(item.navItem));
  }

  // Les comptes sans clinique (invités, clients) n'ont accès qu'aux études, comptes-rendus et albums
  // Les comptes client n'ont pas accès aux albums
  private applyGuestRestrictions(): void {
    this.hasNoClinics = this.clinics.length === 0;
    if (this.hasNoClinics && this.preferences?.preferenciesObject) {
      const isClient = this.appState.claims.includes('roles=Client');
      const allowedItems = isClient
        ? [NavItemName.REPORTS]
        : [NavItemName.REPORTS, NavItemName.ATLAS];

      this.preferences.preferenciesObject.navPref = this.preferences.preferenciesObject.navPref
        .filter(n => allowedItems.includes(n.navItem))
        .map(n => ({ ...n, pinned: true }));
      this.filterPinnedItems();
    }
  }

  private applyClinic(clinicId: number): void {
    const userId = this.tokenStorage.getUserId();
    if (userId) {
      // Use admin service if user is admin, otherwise use regular user clinics
      if (this.isAdmin) {
        const clinicSource$ = this.isScc
          ? this.adminClinicService.getAllClinics()
          : this.adminClinicService.getClinicsWithModality();

        clinicSource$.subscribe((adminClinics: ClinicWithOrthancModalityInterface[]) => {
          const allMapped = adminClinics
            .map(clinic => ({
              id: clinic.id,
              name: clinic.name,
              shortName: clinic.shortName,
              parent: (clinic.parentName && clinic.parentName.trim() !== '') ? { id: -1, name: clinic.parentName } : undefined,
              city: '',
              phone: '',
              logoUrl: '',
              isGroup: clinic.isGroup,
              isMyPacs: clinic.isMyPacs,
              hasMyPacsAccess: clinic.hasMyPacsAccess,
              isLite: clinic.isLite,
              hasSisters: false,
              hasPaymentMethod: false
            } as ClinicInterface));

          if (this.isScc) {
            this.clinics = allMapped.filter(c => !c.isMyPacs);
            this.myPacsClinics = allMapped.filter(c => c.isMyPacs);
            this.filteredMyPacsClinics = [...this.myPacsClinics];
          } else {
            this.clinics = allMapped;
            this.myPacsClinics = [];
          }


          this.filteredClinics = [...this.clinics];
          this.groupClinics();

          // pour gerer les utilisateurs externes (ex client), voir si on peut faire mieux
          if (this.clinics.length === 0 && this.myPacsClinics.length === 0) {
            this.appState.clinicId = -1;
          }

          this.applyGuestRestrictions();

          const currentClinic = this.clinics.find((clinic) => clinic.id === clinicId)
            ?? this.myPacsClinics.find((clinic) => clinic.id === clinicId)
            ?? this.clinics[0];
          if (currentClinic) {
            this.selectedClinic = currentClinic;

            this.clinicName = currentClinic.name;

            this.appState.clinicId = currentClinic.id;
            this.appState.clinic = currentClinic;
          }
        });
      } else {
        this.clinicService.userClinics(userId, !this.appState.isScc).subscribe((clinics) => {
          let mappedClinics = clinics.map(clinic=> ({ ...clinic }));

          // En mode SCC, ne garder que les cliniques avec le role SccVeterinary
          if (this.isScc && this.appState.roles) {
            const roles = this.appState.roles;
            mappedClinics = mappedClinics.filter(c => roles.includes(`SccVeterinary_${c.id}`));
          }

          this.clinics = mappedClinics;
          this.filteredClinics = [...this.clinics];
          this.groupClinics();

          // Gérer les utilisateurs externes (ex client) qui n'ont aucune clinique
          // Ne pas remettre à -1 si un clinicId valide est déjà défini (cas SCC avec nouvelle clinique créée)
          const currentAppStateClinicId = this.appState.clinicId;
          if (this.clinics.length === 0 && (!currentAppStateClinicId || currentAppStateClinicId <= 0)) {
            this.appState.clinicId = -1;
          }

          this.applyGuestRestrictions();

          const currentClinic = this.clinics.find((clinic) => clinic.id === clinicId) ?? this.clinics[0];
          if (currentClinic) {
            this.selectedClinic = currentClinic;

            this.clinicName = currentClinic.name;

            // Protection contre l'écrasement du clinicId lors d'une inscription SCC
            // Si une nouvelle clinique vient d'être créée, elle pourrait ne pas encore apparaître dans userClinics()
            const currentAppStateClinicId = this.appState.clinicId;
            const clinicExistsInList = this.clinics.find(c => c.id === currentAppStateClinicId);

            if (!currentAppStateClinicId || clinicExistsInList) {
              this.appState.clinicId = currentClinic.id;
              this.appState.clinic = currentClinic;
            }
          }
        });
      }
    }
  }

  /**
   * Gère l'ajout d'une nouvelle clinique
   * @param newClinic La nouvelle clinique ajoutée
   */
  private onClinicAdded(newClinic: ClinicWithOrthancModalityInterface): void {
    // Convertit la clinique admin en clinique standard pour la navigation
    const clinic: ClinicInterface = {
      id: newClinic.id,
      name: newClinic.name,
      logoUrl: newClinic.logoUrl,
      shortName: newClinic.shortName,
    };

    // Ajoute la nouvelle clinique à la liste si elle n'existe pas déjà
    const existingClinic = this.clinics.find(c => c.id === clinic.id);
    if (!existingClinic) {
      this.clinics = [...this.clinics, clinic];
      this.filteredClinics = [...this.clinics];
      this.groupClinics();
    }
  }


  private updateFinalizingState(url: string): void {
    this.isFinalizingAccount = url.startsWith('/confirm-clinic') || url.startsWith('/register');
  }

  private setTrad(): void {
    marker('mainNav.en');
    marker('mainNav.fr');
    marker('mainNav.notificationsMenu.text.warn_import_worklist_exist');
    marker('mainNav.notificationsMenu.text.err_import_worklist_patient_not_found');
    marker('mainNav.notificationsMenu.text.err_import_worklist_modality_not_found');
    marker('mainNav.notificationsMenu.text.warn_import_worklist_modality_type_different');
    marker('mainNav.snackbar.share_album');
  }
}
